import React, { useEffect, useState } from 'react';
import { chatSessionAPI, spacesAPI, mcpAPI, agentRunAPI } from '../services/api.js';

const T = {
  borderPrimary: 'var(--lyra-color-border)',
  borderSubtle: 'var(--app-surface-3)',
  textSoft: 'var(--app-text-3)',
  text: 'var(--lyra-color-text)',
  textMid: '#4B5563',
  bg: 'var(--lyra-color-surface)',
  bgSubtle: 'var(--lyra-color-surface-2)',
  accent: '#2E6B5A',
  accentSoft: 'var(--lyra-color-surface-2)',
};

const TAB_LABEL = {
  config: 'Config',
  memory: 'Memory',
  history: 'History',
  members: 'Members',
  agents: 'Agents',
  activity: 'Activity',
  integrations: 'Integrations',
};

export default function ContextPanel({ conversation }) {
  const [tab, setTab] = useState('');
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);

  useEffect(() => {
    if (!conversation) return;
    setTab(conversation.type === 'personal' ? 'config' : 'members');
    setData(null);
  }, [conversation]);

  useEffect(() => {
    if (!conversation) return;
    let cancelled = false;
    (async () => {
      try {
        if (conversation.type === 'personal') {
          if (tab === 'config' || tab === 'memory' || tab === 'history') {
            const r = await chatSessionAPI.get(conversation.id);
            if (cancelled) return;
            setData(r || null);
          }
        } else {
          if (tab === 'members' || tab === 'agents' || tab === 'activity' || tab === 'integrations') {
            const r = await spacesAPI.get(conversation.id);
            if (cancelled) return;
            setData(r.space || null);
            if (tab === 'integrations') {
              // load connected capabilities/tools for space owners (best-effort)
              try { const tools = await mcpAPI.listServers(); } catch (_) {}
            }
            if (tab === 'activity') {
              // load recent agent runs for this space (best-effort)
              try {
                const runsRes = await agentRunAPI.listAll({ limit: 20, spaceId: conversation.id });
                if (!cancelled) setRuns(runsRes.runs || runsRes || []);
              } catch (_) { if (!cancelled) setRuns([]); }
            }
          }
        }
      } catch (err) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [conversation, tab]);

  if (!conversation) {
    return <div style={{ fontSize: 12, color: T.textSoft }}>Select a conversation to view context</div>;
  }

  const tabs = conversation.type === 'personal'
    ? ['config', 'memory', 'history']
    : ['members', 'agents', 'activity', 'integrations'];

  const sectionTitle = conversation.type === 'personal'
    ? (conversation.title || 'Conversation')
    : (conversation.title || 'Space');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ padding: '10px 12px', borderRadius: 10, background: T.bgSubtle, border: `1px solid ${T.borderSubtle}` }}>
        <div style={{ fontSize: 11, color: T.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Context</div>
        <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginTop: 4 }}>{sectionTitle}</div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              border: `1px solid ${tab === t ? '#BDE5D8' : T.borderPrimary}`,
              background: tab === t ? T.accentSoft : 'white',
              color: tab === t ? T.accent : T.textMid,
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {TAB_LABEL[t] || t}
          </button>
        ))}
      </div>

      <div style={{ background: T.bg, padding: 12, borderRadius: 12, border: `1px solid ${T.borderPrimary}`, boxShadow: '0 1px 2px rgba(16,24,40,0.04)' }}>
        {conversation.type === 'personal' ? (
          <div>
            {tab === 'config' && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{conversation.title}</div>
                <div style={{ fontSize: 12, color: T.textSoft, marginTop: 6 }}>Model, system prompt, and configuration</div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: T.textMid }}>Model: <strong style={{ color: T.text }}>{data?.systemPrompt?.model || data?.model || 'default'}</strong></div>
                  <div style={{ fontSize: 12, marginTop: 8, color: T.textMid }}>System prompt: <span style={{ color: T.text }}>{data?.systemPrompt || '—'}</span></div>
                </div>
              </div>
            )}
            {tab === 'memory' && (
              <div>
                <div style={{ fontSize: 12, color: T.textSoft }}>Attached memory context</div>
                <div style={{ marginTop: 8, fontSize: 13, color: T.text }}>{data?.messages ? `${data.messages.length} messages` : '—'}</div>
              </div>
            )}
            {tab === 'history' && (
              <div>
                <div style={{ fontSize: 12, color: T.textSoft }}>Session history</div>
                <div style={{ marginTop: 8, fontSize: 13, color: T.text }}>{data ? `${data.messageCount || (data.messages || []).length} messages · Created ${new Date(data.createdAt).toLocaleString()}` : 'Loading…'}</div>
              </div>
            )}
          </div>
        ) : (
          <div>
            {tab === 'members' && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{conversation.title}</div>
                <div style={{ fontSize: 12, color: T.textSoft, marginTop: 6 }}>{data ? `${data.members?.length || 0} members` : 'Loading…'}</div>
                <div style={{ marginTop: 10 }}>
                  {(data?.members || []).map((m, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, padding: '8px 9px', borderRadius: 10, border: `1px solid ${T.borderSubtle}`, background: T.bgSubtle }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--lyra-color-surface-2)', color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(m.lyranName||'?').charAt(0)}</div>
                      <div style={{ fontSize: 12, color: T.text }}>{m.lyranName} <div style={{ fontSize: 11, color: T.textSoft }}>{m.role}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {tab === 'agents' && (
              <div>
                <div style={{ fontSize: 12, color: T.textSoft }}>Active agents</div>
                <div style={{ marginTop: 8, fontSize: 13, color: T.text }}>{/* TODO: list agents from currentTask */}{data?.currentTask?.runningLyrans?.join(', ') || '—'}</div>
              </div>
            )}
            {tab === 'activity' && (
              <div>
                <div style={{ fontSize: 12, color: T.textSoft }}>Recent activity</div>
                <div style={{ marginTop: 8 }}>
                  {runs === null ? (
                    <div style={{ fontSize: 12, color: T.textSoft }}>Loading…</div>
                  ) : runs.length === 0 ? (
                    <div style={{ fontSize: 12, color: T.textSoft }}>No recent runs</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {runs.map((run) => (
                        <div key={run._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 10, border: `1px solid ${T.borderSubtle}`, background: T.bgSubtle }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--lyra-color-surface-2)', color: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>{(run.agentName||'?').charAt(0)}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{run.agentName || (run.chainId ? 'Workflow' : 'Agent')}</div>
                              <div style={{ fontSize: 11, color: T.textSoft }}>{run.status} · {run.createdAt ? new Date(run.createdAt).toLocaleString() : ''}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ fontSize: 12, color: T.textSoft, marginRight: 8 }}>{run.durationMs ? `${Math.round((run.durationMs||0)/1000)}s` : ''}</div>
                            <button onClick={() => setSelectedRunId(run._id)} style={{ padding: '6px 8px', borderRadius: 8, background: 'var(--lyra-color-surface)', border: `1px solid ${T.borderPrimary}`, color: T.textMid, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Details</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {tab === 'integrations' && (
              <div>
                <div style={{ fontSize: 12, color: T.textSoft }}>Connected Integrations</div>
                <div style={{ marginTop: 8, fontSize: 13, color: T.text }}>Manage connected capabilities and integrations here.</div>
              </div>
            )}
          </div>
        )}
        {/* Run details panel */}
        {selectedRunId && (
          <RunDetails runId={selectedRunId} onClose={() => setSelectedRunId(null)} />
        )}
      </div>
    </div>
  );
}

function RunDetails({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let es = null;

    const fetchRun = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/agent-runs/${runId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } });
        const data = await res.json().catch(() => null);
        if (cancelled) return;
        if (data && data.success) setRun(data.run);
      } catch (err) {}
      setLoading(false);
    };

    fetchRun().then(() => {
      try {
        const token = encodeURIComponent(localStorage.getItem('authToken') || '');
        es = new EventSource(`/api/agent-runs/${runId}/stream?token=${token}`);

        es.onmessage = (ev) => {
          try {
            const payload = JSON.parse(ev.data);
            if (payload.event === 'connected') return;
            // append human-readable log entry
            const entry = `[${new Date(payload.ts || Date.now()).toLocaleTimeString()}] ${payload.event}: ${JSON.stringify(payload.payload)}`;
            setLogs((s) => [...s, entry]);

            // If payload carries an updated run object, merge it
            if (payload.payload && payload.payload.run) {
              setRun((r) => Object.assign({}, r || {}, payload.payload.run));
            }

            // update run status if finished
            if (payload.event === 'run_finished' || payload.payload?.status) {
              const status = payload.payload?.status || (payload.event === 'run_finished' ? 'success' : null);
              if (status && run && run.status === 'running') setRun((r) => Object.assign({}, r, { status }));
            }
          } catch (e) {}
        };

        es.onerror = () => {
          // keep the connection alive — EventSource will auto-retry. Do nothing here.
        };
      } catch (e) {
        // fail silently — SSE is best-effort
      }
    });

    return () => {
      cancelled = true;
      if (es) try { es.close(); } catch (_) {}
    };
  }, [runId]);

  if (loading) return <div style={{ marginTop: 12, fontSize: 12, color: T.textSoft }}>Loading run…</div>;
  if (!run) return <div style={{ marginTop: 12, fontSize: 12, color: T.textSoft }}>Run not found</div>;

  return (
    <div style={{ marginTop: 12, padding: 12, borderRadius: 12, border: `1px solid ${T.borderPrimary}`, background: 'var(--lyra-color-surface-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{run.agentName || 'Run'}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ fontSize: 12, color: T.textSoft }}>{run.status}</div>
          <button onClick={onClose} style={{ padding: '6px 8px', borderRadius: 8, border: `1px solid ${T.borderPrimary}`, color: T.textMid, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Close</button>
        </div>
      </div>

      <div style={{ fontSize: 12, marginBottom: 8, color: T.textMid }}><strong style={{ color: T.text }}>Input:</strong> {run.input || '—'}</div>
      <div style={{ fontSize: 12, marginBottom: 8, color: T.textMid }}><strong style={{ color: T.text }}>Output:</strong></div>
      <pre style={{ background: 'white', padding: 8, borderRadius: 8, border: `1px solid ${T.borderPrimary}`, maxHeight: 220, overflow: 'auto', fontSize: 12, color: T.text }}>{run.output || run.error || '—'}</pre>

      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 6 }}>Live logs</div>
        <div style={{ background: 'black', color: 'white', padding: 8, borderRadius: 6, maxHeight: 220, overflow: 'auto', fontSize: 12 }}>
          {logs.length === 0 ? <div style={{ color: 'var(--app-text-3)' }}>No live events yet</div> : (
            logs.map((l, i) => <div key={i} style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}>{l}</div>)
          )}
        </div>
      </div>

      {run.steps && run.steps.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: T.textSoft, marginBottom: 6 }}>Steps</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {run.steps.map((s, i) => (
              <div key={i} style={{ padding: 8, borderRadius: 8, background: 'white', border: `1px solid ${T.borderPrimary}` }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{s.name}</div>
                <div style={{ fontSize: 11, color: T.textSoft }}>{s.status} · {s.description}</div>
                <div style={{ fontSize: 11, marginTop: 6, color: T.textMid }}>{s.startedAt ? `Started: ${new Date(s.startedAt).toLocaleString()}` : ''} {s.completedAt ? ` · Completed: ${new Date(s.completedAt).toLocaleString()}` : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
